import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IconCheck, IconColumns } from "@/components/icons";
import { useClickOutside } from "@/hooks/useClickOutside";
import { parseStaffColumnKey, staffColumnKey } from "@/lib/forms/staffColumns";
import { StaffColumnsSettings } from "@/components/admin/StaffColumnsSettings";
import type { StaffColumnDto } from "@/types/staffColumn";

export type SubmissionsFieldOption = {
  id: string;
  label: string;
};

export type SubmissionsMarksSettings = {
  formId: number;
  columns: StaffColumnDto[];
  canManage?: boolean;
  onColumnsChange: (columns: StaffColumnDto[]) => void;
};

export function SubmissionsFieldsPicker({
  fields,
  hiddenIds,
  onChange,
  marks,
}: {
  fields: SubmissionsFieldOption[];
  hiddenIds: Set<string>;
  onChange: (hiddenIds: Set<string>) => void;
  marks?: SubmissionsMarksSettings;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"fields" | "marks">("fields");
  const [query, setQuery] = useState("");
  const [draftIds, setDraftIds] = useState<Set<string>>(() => new Set());

  const close = useCallback(() => setOpen(false), []);
  useClickOutside(containerRef, close, open);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setTab("fields");
    const visible = new Set(
      fields
        .filter((field) => !hiddenIds.has(field.id))
        .map((field) => field.id),
    );
    for (const column of marks?.columns ?? []) {
      const key = staffColumnKey(column.id);
      if (!hiddenIds.has(key)) visible.add(key);
    }
    setDraftIds(visible);
  }, [fields, hiddenIds, marks?.columns, open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [close, open]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return fields;
    return fields.filter((field) => field.label.toLowerCase().includes(needle));
  }, [fields, query]);

  if (fields.length === 0 && !marks) return null;

  const toggle = (fieldId: string) => {
    setDraftIds((current) => {
      const next = new Set(current);
      if (next.has(fieldId)) next.delete(fieldId);
      else next.add(fieldId);
      return next;
    });
  };

  const handleMarksChange = (columns: StaffColumnDto[]) => {
    marks?.onColumnsChange(columns);
    setDraftIds((current) => {
      const next = new Set(current);
      const keep = new Set(columns.map((column) => column.id));
      for (const key of [...next]) {
        const id = parseStaffColumnKey(key);
        if (id != null && !keep.has(id)) next.delete(key);
      }
      for (const column of columns) {
        next.add(staffColumnKey(column.id));
      }
      return next;
    });
  };

  const save = () => {
    const hidden = new Set(
      fields
        .filter((field) => !draftIds.has(field.id))
        .map((field) => field.id),
    );
    for (const column of marks?.columns ?? []) {
      const key = staffColumnKey(column.id);
      if (!draftIds.has(key)) hidden.add(key);
    }
    onChange(hidden);
    close();
  };

  const selectedMarkIds = new Set(
    (marks?.columns ?? [])
      .filter((column) => draftIds.has(staffColumnKey(column.id)))
      .map((column) => column.id),
  );

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((current) => !current)}
        className="inline-flex h-8 max-w-[16rem] items-center gap-1.5 rounded-lg bg-slate-100 px-2.5 font-onest text-xs font-medium text-slate-700 hover:bg-slate-200/80"
      >
        <IconColumns size={14} className="shrink-0 text-slate-500" />
        <span className="truncate">Столбцы</span>
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Столбцы таблицы ответов"
          className="absolute left-0 z-50 mt-1.5 w-[22.5rem] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg"
        >
          {marks ? (
            <div className="grid grid-cols-2 border-b border-slate-100 p-1">
              <button
                type="button"
                onClick={() => setTab("fields")}
                className={[
                  "rounded-lg py-1.5 font-onest text-xs font-medium",
                  tab === "fields"
                    ? "bg-slate-100 text-slate-800"
                    : "text-slate-500 hover:text-slate-700",
                ].join(" ")}
              >
                Поля формы
              </button>
              <button
                type="button"
                onClick={() => setTab("marks")}
                className={[
                  "rounded-lg py-1.5 font-onest text-xs font-medium",
                  tab === "marks"
                    ? "bg-slate-100 text-slate-800"
                    : "text-slate-500 hover:text-slate-700",
                ].join(" ")}
              >
                Пометки
                {marks.columns.length > 0 ? (
                  <span className="ml-1 text-slate-400">
                    {marks.columns.length}
                  </span>
                ) : null}
              </button>
            </div>
          ) : null}

          {tab === "fields" || !marks ? (
            <>
              <div className="flex items-center gap-2 p-3">
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Поиск по столбцам"
                  className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 font-onest text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="shrink-0 rounded-lg bg-slate-100 px-3 py-1.5 font-onest text-sm font-medium text-slate-700 hover:bg-slate-200/80"
                >
                  Очистить
                </button>
              </div>

              <ul className="max-h-56 overflow-y-auto px-1 pb-1">
                {filtered.length === 0 ? (
                  <li className="px-3 py-4 text-center font-onest text-sm text-slate-500">
                    Ничего не найдено
                  </li>
                ) : (
                  filtered.map((field) => {
                    const selected = draftIds.has(field.id);
                    return (
                      <li key={field.id}>
                        <button
                          type="button"
                          onClick={() => toggle(field.id)}
                          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left hover:bg-slate-50"
                        >
                          <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                            {selected ? (
                              <IconCheck size={16} className="text-brand-500" />
                            ) : (
                              <span className="h-3.5 w-3.5 rounded-sm border border-slate-300" />
                            )}
                          </span>
                          <span
                            className="min-w-0 truncate font-onest text-sm text-slate-800"
                            title={field.label}
                          >
                            {field.label}
                          </span>
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
            </>
          ) : (
            <div className="max-h-[22rem] overflow-y-auto p-3">
              <p className="mb-3 font-onest text-xs leading-4 text-slate-500">
                Свои столбцы справа в таблице. В форму, ответы и вебхуки не
                попадают.
              </p>
              <StaffColumnsSettings
                formId={marks.formId}
                columns={marks.columns}
                onColumnsChange={handleMarksChange}
                canManage={marks.canManage !== false}
                selectedIds={selectedMarkIds}
                onToggleSelected={(columnId) =>
                  toggle(staffColumnKey(columnId))
                }
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 border-t border-slate-100 p-3">
            <button
              type="button"
              onClick={close}
              className="rounded-lg bg-slate-100 py-2 font-onest text-sm font-medium text-slate-700 hover:bg-slate-200/80"
            >
              Отменить
            </button>
            <button
              type="button"
              onClick={save}
              className="rounded-lg bg-brand-500 py-2 font-onest text-sm font-medium text-white hover:bg-brand-600"
            >
              Сохранить
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
